import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _uniqueId from "lodash/uniqueId";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { View, Text, Page } from '@react-pdf/renderer';
import { notesIncluded } from '@mummssoftware/utils/utils';
import { styles } from '../../utils/styles';
import { RoleGroupPDF } from './components/RoleGroupPDF';
import { CareProviderPDF } from './components/CareProviderPDF';
import { FrequencyPDF } from './components/FrequencyPDF';
import { PrnPDF } from './components/PrnPDF';
import { EffectivePDF } from './components/EffectivePDF';
import { CpfNotesPDF } from './components/CpfNotesPDF';
import { fromDateToDate, ios, pageOrientation, pageSize, safari } from '../../utils';
import { SpecificDaysPDF } from './components/SpecificDaysPDF';
import { PrintHeaderPDFComponent } from '../Header';
import { PrintFooterPDFComponent } from '../Footer';
import defaults from '../../translations/en.json';

const NoCPFDataComponent = ({
  rgHash,
  dashboardPrintConfig
}) => /*#__PURE__*/React.createElement(View, {
  style: [styles.width100, styles.textAlignCenter]
}, /*#__PURE__*/React.createElement(Text, {
  style: styles.CPFTableDetailsText
}, rgHash && dashboardPrintConfig && dashboardPrintConfig.fromDate && dashboardPrintConfig.toDate ? defaults.notInEffectDuringDateRange : defaults.noCarePlans));

var _ref = /*#__PURE__*/React.createElement(CareProviderPDF, {
  assignedRoles: null
});

var _ref2 = /*#__PURE__*/React.createElement(SpecificDaysPDF, {
  carePlan: null
});

const CPFHistoricalRow = ({
  row
}) => /*#__PURE__*/React.createElement(View, {
  key: _uniqueId('care-plan-frequency-historical-row'),
  style: [styles.row, styles.width100, styles.borderBottomBlack, styles.pYPointOneCm]
}, /*#__PURE__*/React.createElement(RoleGroupPDF, {
  roleGroup: row.discipline
}), _ref, /*#__PURE__*/React.createElement(FrequencyPDF, {
  carePlan: row
}), _ref2, /*#__PURE__*/React.createElement(PrnPDF, {
  isPrn: row.prn
}), /*#__PURE__*/React.createElement(EffectivePDF, {
  carePlan: row
}));

const CPFTableRows = ({
  rgHash,
  rgHashPrint,
  dashboardPrintConfig,
  historicalRows
}) => {
  // iterate over each active row
  const rowUI = rgHashPrint.map(assignment => {
    // look up table to find the care plan frequency object for this assignment (assignment object has person role type and patient info)
    const carePlan = rgHash && rgHash[assignment.roleGroupId.toString()].frequencies[assignment.id]; // list of objects with individual person data

    const assignedRoles = rgHash && Object.values(rgHash[assignment.roleGroupId.toString()].pimAssignments || {});
    const isPrn = rgHash && rgHash[assignment.roleGroupId.toString()].prn || false;

    const uniqueId = _uniqueId('care-plan-frequency-row');

    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
      key: uniqueId,
      style: [styles.row, styles.width100, styles.borderBottomBlack, styles.pYPointOneCm]
    }, /*#__PURE__*/React.createElement(RoleGroupPDF, {
      roleGroup: assignment.name
    }), /*#__PURE__*/React.createElement(CareProviderPDF, {
      assignedRoles: assignedRoles
    }), /*#__PURE__*/React.createElement(FrequencyPDF, {
      carePlan: carePlan
    }), /*#__PURE__*/React.createElement(SpecificDaysPDF, {
      carePlan: carePlan
    }), /*#__PURE__*/React.createElement(PrnPDF, {
      isPrn: isPrn
    }), /*#__PURE__*/React.createElement(EffectivePDF, {
      carePlan: carePlan
    })), // find older care plans with this role type & render
    historicalRows && historicalRows.map(el => el.discipline === assignment.name && /*#__PURE__*/React.createElement(CPFHistoricalRow, {
      row: el
    })));
  }).filter(row => row !== undefined);
  return /*#__PURE__*/React.createElement(View, null, rowUI.length > 0 && rowUI || /*#__PURE__*/React.createElement(NoCPFDataComponent, {
    dashboardPrintConfig: dashboardPrintConfig,
    rgHash: rgHash
  }));
};

var _ref3 = /*#__PURE__*/React.createElement(PrintFooterPDFComponent, null);

export const CPFTablePDF = ({
  rgHashPrint,
  rgHash,
  dashboardPrintConfig,
  cpfNotes,
  browser,
  patient,
  historicalRows
}) => {
  const shallowDashboardPrintConfigView = dashboardPrintConfig && _objectSpread({}, dashboardPrintConfig);

  const CPFPrintView = shallowDashboardPrintConfigView === null || shallowDashboardPrintConfigView === void 0 ? void 0 : shallowDashboardPrintConfigView.CarePlanFrequency;
  return /*#__PURE__*/React.createElement(Page, {
    wrap: false,
    size: pageSize,
    orientation: pageOrientation,
    style: browser && browser.name === safari || browser && browser.name === ios ? styles.pageSafari : styles.page
  }, /*#__PURE__*/React.createElement(PrintHeaderPDFComponent, {
    browser: browser,
    patient: patient
  }), /*#__PURE__*/React.createElement(View, {
    style: [styles.textAlignCenter, styles.width100, styles.marginBottomPointFiveCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: styles.printerHeaderText
  }, defaults.carePlanFrequency), dashboardPrintConfig && dashboardPrintConfig.fromDate && dashboardPrintConfig.toDate && /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontFamilyItalic, styles.fontSizeTenPt, styles.marginYPointTwoCm]
  }, fromDateToDate(dashboardPrintConfig))), /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.borderBottomBlack]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width20]
  }, defaults.roleGroup), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width20]
  }, defaults.careProvider), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width20]
  }, defaults.frequency), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width20]
  }, defaults.specificDays), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width10]
  }, `+ ${defaults.prn}`), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width10]
  }, defaults.effective)), /*#__PURE__*/React.createElement(CPFTableRows, {
    rgHashPrint: rgHashPrint,
    rgHash: rgHash,
    dashboardPrintConfig: dashboardPrintConfig,
    historicalRows: historicalRows
  }), notesIncluded(CPFPrintView) && /*#__PURE__*/React.createElement(CpfNotesPDF, {
    rgHash: rgHash,
    cpfNotes: cpfNotes,
    dashboardPrintConfig: dashboardPrintConfig
  }), _ref3);
};